import classNames from "classnames";
import { useQuery } from "@apollo/client";
import { useState } from "react";
import { type Path } from "@/router";
import useCapabilities, { useCanViewCareerServicesDashboardBool } from "@/hooks/useCapabilities";
import {
  ASSOCIATE_DASHBOARD_CELEBRATIONS_QUERY,
  ASSOCIATE_DASHBOARD_PACING_QUERY,
  ASSOCIATE_DASHBOARD_SIGNALS_QUERY,
  type AssociateDashboardCelebrationsQueryVariables,
  type AssociateDashboardPacingQueryVariables,
  type AssociateDashboardSignalsQueryVariables
} from "../queries";
import {
  toDashboardAssociateCelebrationGroups,
  toDashboardAssociatePacingRows,
  toDashboardAssociateSignalGroups
} from "../_utils/associateDashboardDisplay";
import { useCareerServicesDashboardSectionSelection } from "../_hooks/useCareerServicesDashboardSectionSelection";
import useSectionScopedQueryData from "../_hooks/useSectionScopedQueryData";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";
import DashboardPacingPanel from "./DashboardPacingPanel";
import DashboardSignalTable from "./DashboardSignalTable";
import CareerServicesDashboardSectionFilter from "./CareerServicesDashboardSectionFilter";
import CareerServicesDashboardSectionPrompt from "./CareerServicesDashboardSectionPrompt";
import CareerServicesDashboardGate from "./CareerServicesDashboardGate";
import DashboardTimeframeToggle from "./DashboardTimeframeToggle";
import { DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS } from "../_utils/dashboardPanelStyles";
import { DEFAULT_DASHBOARD_WINDOW } from "../_utils/dashboardTimeframe";

const ASSOCIATE_DASHBOARD_PREVIEW_LIMIT = 3;

function AssociatesDashboardContent() {
  const [dashboardWindow, setDashboardWindow] = useState(DEFAULT_DASHBOARD_WINDOW);
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerServicesDashboard = useCanViewCareerServicesDashboardBool();
  const {
    sectionId,
    ready: sectionScopeReady,
    requiresSectionSelection
  } = useCareerServicesDashboardSectionSelection();
  const sectionSelectionRequired = requiresSectionSelection && !sectionId;
  const signalVariables: AssociateDashboardSignalsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: ASSOCIATE_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const celebrationVariables: AssociateDashboardCelebrationsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: ASSOCIATE_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const pacingVariables: AssociateDashboardPacingQueryVariables = {
    input: {
      ...(sectionId ? { sectionId } : {})
    }
  };
  const { data, previousData, loading, networkStatus, error } = useQuery(ASSOCIATE_DASHBOARD_SIGNALS_QUERY, {
    variables: signalVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard || !sectionScopeReady,
    fetchPolicy: "cache-and-network"
  });
  const {
    data: celebrationData,
    previousData: previousCelebrationData,
    loading: celebrationLoading,
    networkStatus: celebrationNetworkStatus,
    error: celebrationError
  } = useQuery(ASSOCIATE_DASHBOARD_CELEBRATIONS_QUERY, {
    variables: celebrationVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard || !sectionScopeReady,
    fetchPolicy: "cache-and-network"
  });
  const {
    data: pacingData,
    previousData: previousPacingData,
    loading: pacingLoading,
    networkStatus: pacingNetworkStatus,
    error: pacingError
  } = useQuery(ASSOCIATE_DASHBOARD_PACING_QUERY, {
    variables: pacingVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard || !sectionScopeReady,
    fetchPolicy: "cache-and-network"
  });
  const dashboardSignals = useSectionScopedQueryData({
    sectionId,
    data: data?.associateDashboardSignals,
    previousData: previousData?.associateDashboardSignals,
    loading,
    enabled: capabilitiesLoaded && canViewCareerServicesDashboard && sectionScopeReady
  });
  const dashboardCelebrations = useSectionScopedQueryData({
    sectionId,
    data: celebrationData?.associateDashboardCelebrations,
    previousData: previousCelebrationData?.associateDashboardCelebrations,
    loading: celebrationLoading,
    enabled: capabilitiesLoaded && canViewCareerServicesDashboard && sectionScopeReady
  });
  const dashboardPacing = useSectionScopedQueryData({
    sectionId,
    data: pacingData?.associateDashboardPacing,
    previousData: previousPacingData?.associateDashboardPacing,
    loading: pacingLoading,
    enabled: capabilitiesLoaded && canViewCareerServicesDashboard && sectionScopeReady
  });
  const associateSignalGroups = toDashboardAssociateSignalGroups(dashboardSignals?.signals ?? []);
  const associateCelebrationGroups = toDashboardAssociateCelebrationGroups(dashboardCelebrations?.celebrations ?? []);
  const associatePacingRows = toDashboardAssociatePacingRows(dashboardPacing?.levels ?? []);
  const totalAssociateRows = associateSignalGroups.reduce((total, group) => total + group.rows.length, 0);
  const totalAssociateCelebrationRows = associateCelebrationGroups.reduce(
    (total, group) => total + group.rows.length,
    0
  );
  const dashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard && !sectionScopeReady) ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading,
        networkStatus,
        itemCount: totalAssociateRows,
        hasData: dashboardSignals !== undefined,
        error
      }));
  const celebrationDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard && !sectionScopeReady) ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading: celebrationLoading,
        networkStatus: celebrationNetworkStatus,
        itemCount: totalAssociateCelebrationRows,
        hasData: dashboardCelebrations !== undefined,
        error: celebrationError
      }));
  const pacingDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard && !sectionScopeReady) ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading: pacingLoading,
        networkStatus: pacingNetworkStatus,
        itemCount: associatePacingRows.length,
        hasData: dashboardPacing !== undefined,
        error: pacingError
      }));
  const dashboardUnavailable = capabilitiesLoaded && !canViewCareerServicesDashboard;
  const dashboardError = Boolean(error && !dashboardSignals);
  const celebrationDashboardError = Boolean(celebrationError && !dashboardCelebrations);
  const pacingDashboardError = Boolean(pacingError && !dashboardPacing);

  return (
    <div
      className={classNames("flex flex-col gap-6", DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS)}
      data-testid="home-associates-dashboard"
    >
      <div
        className="flex w-full flex-wrap items-center justify-between gap-6"
        data-testid="home-associates-timeframe-row"
      >
        <DashboardTimeframeToggle
          value={dashboardWindow}
          onChange={setDashboardWindow}
          testId="home-associates-timeframe"
        />
        <CareerServicesDashboardSectionFilter />
      </div>

      {sectionSelectionRequired ? <CareerServicesDashboardSectionPrompt /> : null}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3" hidden={sectionSelectionRequired}>
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <DashboardSignalTable
            title="Needs Attention"
            icon="warning"
            testId="home-associates-needs-attention"
            tableTestId="home-associates-needs-attention-table"
            tableAriaLabel="Associate needs attention"
            groupTestIdPrefix="home-associates-needs-attention-group"
            rowTestIdPrefix="home-associate-needs-attention-row"
            personLabel="associate"
            groups={associateSignalGroups}
            loading={dashboardLoading}
            error={dashboardError}
            unavailable={dashboardUnavailable}
            viewAll={{
              label: "View all associates",
              to: "/people?programTypes%5B%5D=ASSOCIATE" as Path,
              testId: "home-associates-view-all"
            }}
          />
          <DashboardSignalTable
            title="Celebrations"
            icon="celebration"
            testId="home-associates-celebrations"
            tableTestId="home-associates-celebrations-table"
            tableAriaLabel="Associate celebrations"
            groupTestIdPrefix="home-associates-celebrations-group"
            rowTestIdPrefix="home-associate-celebration-row"
            personLabel="associate"
            groups={associateCelebrationGroups}
            loading={celebrationDashboardLoading}
            error={celebrationDashboardError}
            unavailable={dashboardUnavailable}
          />
        </div>

        <aside className="flex w-full flex-col gap-4" data-testid="home-associates-pacing">
          <DashboardPacingPanel
            title="Associate Pacing"
            progress={associatePacingRows}
            testId="home-associates-progress"
            loading={pacingDashboardLoading}
            error={pacingDashboardError}
            unavailable={dashboardUnavailable}
          />
        </aside>
      </div>
    </div>
  );
}

export default function AssociatesDashboard() {
  return (
    <CareerServicesDashboardGate>
      <AssociatesDashboardContent />
    </CareerServicesDashboardGate>
  );
}
